import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  Truck, 
  CreditCard, 
  CheckCircle, 
  Package,
  MapPin,
  Clock,
  Store,
  BadgePercent,
  ArrowRight
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/design-system';
import { toast } from 'sonner';
import { trackInitiateCheckout, trackPurchase } from '../utils/fbPixel';
import StripeEmbeddedCheckout from '../components/StripeEmbeddedCheckout';
import { FREE_SHIPPING_THRESHOLD, STANDARD_SHIPPING_COST } from '../utils/shipping';
import { apiErrorMessage } from '../utils/helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// List of main Mexican cities with their corresponding states
const MEXICAN_CITIES = [
  { city: 'Aguascalientes', state: 'Aguascalientes' },
  { city: 'Cancún', state: 'Quintana Roo' },
  { city: 'Celaya', state: 'Guanajuato' },
  { city: 'Chihuahua', state: 'Chihuahua' },
  { city: 'Ciudad de México', state: 'Ciudad de México' },
  { city: 'Ciudad Juárez', state: 'Chihuahua' },
  { city: 'Cuernavaca', state: 'Morelos' },
  { city: 'Culiacán', state: 'Sinaloa' },
  { city: 'Durango', state: 'Durango' },
  { city: 'Guadalajara', state: 'Jalisco' },
  { city: 'Hermosillo', state: 'Sonora' },
  { city: 'Irapuato', state: 'Guanajuato' },
  { city: 'León', state: 'Guanajuato' },
  { city: 'Los Mochis', state: 'Sinaloa' },
  { city: 'Mazatlán', state: 'Sinaloa' },
  { city: 'Mérida', state: 'Yucatán' },
  { city: 'Mexicali', state: 'Baja California' },
  { city: 'Monterrey', state: 'Nuevo León' },
  { city: 'Morelia', state: 'Michoacán' },
  { city: 'Oaxaca', state: 'Oaxaca' },
  { city: 'Pachuca', state: 'Hidalgo' },
  { city: 'Playa del Carmen', state: 'Quintana Roo' },
  { city: 'Puebla', state: 'Puebla' },
  { city: 'Querétaro', state: 'Querétaro' },
  { city: 'Saltillo', state: 'Coahuila' },
  { city: 'San Juan del Río', state: 'Querétaro' },
  { city: 'San Luis Potosí', state: 'San Luis Potosí' },
  { city: 'San Miguel de Allende', state: 'Guanajuato' },
  { city: 'Tampico', state: 'Tamaulipas' },
  { city: 'Tequisquiapan', state: 'Querétaro' },
  { city: 'Tijuana', state: 'Baja California' },
  { city: 'Tlaxcala', state: 'Tlaxcala' },
  { city: 'Toluca', state: 'Estado de México' },
  { city: 'Torreón', state: 'Coahuila' },
  { city: 'Tuxtla Gutiérrez', state: 'Chiapas' },
  { city: 'Veracruz', state: 'Veracruz' },
  { city: 'Villahermosa', state: 'Tabasco' },
  { city: 'Zacatecas', state: 'Zacatecas' },
  { city: 'Otra ciudad', state: '' }  // Option for cities not in the list
];

const SHIPPING_METHODS = [
  {
    id: 'standard',
    name: 'Envío Estándar',
    description: '1-3 días hábiles',
    price: STANDARD_SHIPPING_COST,
    freeAbove: FREE_SHIPPING_THRESHOLD
  }
];

const STEPS = [
  { id: 1, name: 'Dirección', icon: MapPin },
  { id: 2, name: 'Envío', icon: Truck },
  { id: 3, name: 'Método', icon: CreditCard },
  { id: 4, name: 'Pago', icon: CreditCard },
  { id: 5, name: 'Confirmación', icon: CheckCircle }
];

const Checkout = () => {
  const navigate = useNavigate();
  const { items, totals, coupon, gifts, sessionId, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null); // For embedded payment
  const [oxxoVoucher, setOxxoVoucher] = useState(null); // For OXXO voucher display

  const [formData, setFormData] = useState({
    // Shipping address
    recipient_name: '',
    email: localStorage.getItem('alphatica_customer_email') || '',
    phone: '',
    street: '',
    neighborhood: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'México',
    // Shipping method
    shipping_method: 'standard',
    // Payment
    payment_method: 'stripe',
    // Notes
    customer_notes: ''
  });

  const [errors, setErrors] = useState({});
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [shippingTotals, setShippingTotals] = useState(null); // For city-based shipping calculation

  // Track InitiateCheckout when user enters checkout with items
  useEffect(() => {
    if (items.length > 0) {
      trackInitiateCheckout(items, totals);
    }
  }, []); // Only on mount

  // Recalculate shipping when city/state changes
  useEffect(() => {
    const fetchShippingCost = async () => {
      if (formData.city && formData.state && sessionId) {
        try {
          const response = await fetch(
            `${API_URL}/api/cart?session_id=${sessionId}&city=${encodeURIComponent(formData.city)}&state=${encodeURIComponent(formData.state)}`
          );
          if (response.ok) {
            const data = await response.json();
            setShippingTotals(data.totals);
          }
        } catch (error) {
          console.error('Error fetching shipping cost:', error);
        }
      }
    };
    
    fetchShippingCost();
  }, [formData.city, formData.state, sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleCityChange = (e) => {
    const selectedCity = e.target.value;
    
    if (selectedCity === 'Otra ciudad') {
      setShowCustomCity(true);
      setFormData(prev => ({ ...prev, city: '', state: '' }));
    } else {
      setShowCustomCity(false);
      const cityData = MEXICAN_CITIES.find(c => c.city === selectedCity);
      if (cityData) {
        setFormData(prev => ({ 
          ...prev, 
          city: cityData.city, 
          state: cityData.state 
        }));
      }
    }
    
    // Clear errors
    if (errors.city) setErrors(prev => ({ ...prev, city: null }));
    if (errors.state) setErrors(prev => ({ ...prev, state: null }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.recipient_name.trim()) newErrors.recipient_name = 'Nombre requerido';
      if (!formData.email.trim()) newErrors.email = 'Correo requerido';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) newErrors.email = 'Correo inválido';
      if (!formData.phone.trim()) newErrors.phone = 'Teléfono requerido';
      if (!formData.street.trim()) newErrors.street = 'Dirección requerida';
      if (!formData.neighborhood.trim()) newErrors.neighborhood = 'Colonia requerida';
      if (!formData.city.trim()) newErrors.city = 'Ciudad requerida';
      if (!formData.state.trim()) newErrors.state = 'Estado requerido';
      if (!formData.postal_code.trim()) newErrors.postal_code = 'Código postal requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  const getShippingCost = () => {
    // If we have city-based shipping totals from backend, use them
    if (shippingTotals && shippingTotals.promo_shipping_applied) {
      return shippingTotals.shipping_cost;
    }
    // Fallback (el servidor es quien manda): envío gratis a todo México desde el umbral
    const subtotal = totals.subtotal - (totals.discount_amount || 0) - (totals.promo_discount || 0);
    if (subtotal >= FREE_SHIPPING_THRESHOLD) {
      return 0;
    }
    
    return STANDARD_SHIPPING_COST;
  };

  const getFinalTotal = () => {
    // If we have city-based totals with promo, use them
    if (shippingTotals && shippingTotals.promo_shipping_applied) {
      const subtotalAfterDiscounts = (shippingTotals.subtotal || 0) - (shippingTotals.promo_discount || 0) - (shippingTotals.discount_amount || 0);
      return subtotalAfterDiscounts + (shippingTotals.shipping_cost || 0);
    }
    // Fallback to standard calculation
    const shippingCost = getShippingCost();
    const subtotalAfterDiscount = totals.subtotal - (totals.discount_amount || 0) - (totals.promo_discount || 0);
    return subtotalAfterDiscount + shippingCost;
  };

  const handleSubmitOrder = async () => {
    // El regalo de 10ml es obligatorio (el servidor también lo exige)
    if (gifts && gifts.applied && !gifts.complete) {
      toast.error('Elige tu perfume de 10ml de regalo antes de pagar');
      navigate('/cart');
      return;
    }
    setLoading(true);
    try {
      // First, create the order in the backend
      const response = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          shipping_address: {
            recipient_name: formData.recipient_name,
            email: formData.email,
            phone: formData.phone,
            street: formData.street,
            neighborhood: formData.neighborhood,
            city: formData.city,
            state: formData.state,
            postal_code: formData.postal_code,
            country: formData.country
          },
          shipping_method: formData.shipping_method,
          payment_method: formData.payment_method,
          customer_notes: formData.customer_notes
        })
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(apiErrorMessage(error, 'Error al procesar la orden'));
        return;
      }

      const data = await response.json();
      
      // For Stripe (card) or OXXO - use embedded checkout
      if (formData.payment_method === 'stripe' || formData.payment_method === 'oxxo') {
        // Store the order and move to embedded payment step
        setCreatedOrder({
          ...data.order,
          // El servidor cobra el total guardado en el pedido: mostrar ese mismo monto
          total: data.order.total ?? getFinalTotal()
        });
        setCurrentStep(4); // Move to payment step
        return;
      }

      // For transfer payment method, show confirmation directly
      setOrderComplete(data.order);
      setCurrentStep(5); // Confirmation step
      await clearCart();
      
      // Track Purchase for Facebook Pixel (transfer payments)
      trackPurchase({
        items: items,
        totals: totals,
        orderId: data.order.id,
        orderNumber: data.order.order_number,
        payment_method: formData.payment_method,
        shipping_method: formData.shipping_method,
        coupon_code: coupon?.code || ''
      });
      
      toast.success('¡Orden creada exitosamente!');
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Handle successful embedded payment
  const handlePaymentSuccess = async (paymentData) => {
    // Track Purchase for Facebook Pixel
    trackPurchase({
      items: items,
      totals: totals,
      orderId: createdOrder?.id,
      orderNumber: createdOrder?.order_number,
      payment_method: paymentData.payment_method || formData.payment_method,
      shipping_method: formData.shipping_method,
      coupon_code: coupon?.code || ''
    });

    // Check if it's OXXO voucher
    if (paymentData.oxxo_details) {
      setOxxoVoucher(paymentData.oxxo_details);
      setOrderComplete(createdOrder);
      setCurrentStep(5);
      await clearCart();
      toast.success('¡Voucher OXXO generado!');
      return;
    }

    // Card payment success
    setOrderComplete(createdOrder);
    setCurrentStep(5);
    await clearCart();
    toast.success('¡Pago exitoso!');
  };

  const handlePaymentError = (errorMessage) => {
    toast.error(errorMessage || 'Error al procesar el pago');
  };

  // Redirect if cart is empty and not on payment/confirmation steps
  if (items.length === 0 && currentStep < 4 && !orderComplete && !createdOrder) {
    return (
      <div className="pt-32 pb-24 px-6 md:px-12 max-w-4xl mx-auto text-center min-h-screen bg-brand-bone">
        <Package className="mx-auto h-16 w-16 text-brand-cobalt/30 mb-6" />
        <h1 className="font-display text-3xl tracking-tight mb-4 text-brand-black">
          Tu carrito está vacío
        </h1>
        <p className="text-brand-black/50 mb-8">
          Agrega productos antes de continuar con el checkout.
        </p>
        <button onClick={() => navigate('/products')} className="brand-btn">
          Ver Productos
        </button>
      </div>
    );
  }

  return (
    <div className="pb-12 px-3 sm:px-4 md:px-6 lg:px-12 max-w-6xl mx-auto min-h-screen bg-brand-bone overflow-x-hidden" data-testid="checkout-page">
      {/* Progress Steps */}
      <div className="mb-8 md:mb-12">
        <div className="flex items-center justify-between md:justify-center md:gap-8 px-2">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`
                    w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all
                    ${isCompleted ? 'bg-green-500 text-white' : ''}
                    ${isActive ? 'bg-brand-cobalt text-white' : ''}
                    ${!isActive && !isCompleted ? 'bg-brand-bone border border-brand-black/20 text-brand-black/40' : ''}
                  `}>
                    {isCompleted ? (
                      <CheckCircle size={18} />
                    ) : (
                      <StepIcon size={18} />
                    )}
                  </div>
                  <span className={`
                    text-[10px] mt-1 hidden sm:block
                    ${isActive ? 'text-brand-cobalt font-medium' : 'text-brand-black/50'}
                  `}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`
                    flex-1 max-w-8 md:max-w-24 h-0.5 mx-1
                    ${currentStep > step.id ? 'bg-green-500' : 'bg-brand-cobalt/20'}
                  `} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-12">
        {/* Main Content */}
        <div className="lg:col-span-2 order-2 lg:order-1 overflow-hidden">
          {/* Step 1: Shipping Address */}
          {currentStep === 1 && (
            <div data-testid="step-address">
              <h2 className="font-display text-xl md:text-2xl tracking-tight mb-4 md:mb-6 text-brand-black">
                Dirección de Envío
              </h2>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                      Nombre completo *
                    </label>
                    <input
                      type="text"
                      name="recipient_name"
                      value={formData.recipient_name}
                      onChange={handleInputChange}
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black ${
                        errors.recipient_name ? 'border-red-500' : 'border-brand-black/20'
                      }`}
                      data-testid="input-recipient-name"
                    />
                    {errors.recipient_name && (
                      <p className="text-red-400 text-xs mt-1">{errors.recipient_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                      Teléfono (WhatsApp) *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+52 "
                      className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black placeholder:text-brand-black/30 ${
                        errors.phone ? 'border-red-500' : 'border-brand-black/20'
                      }`}
                      data-testid="input-phone"
                    />
                    {errors.phone && (
                      <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                    Correo electrónico *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="tu@email.com"
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black placeholder:text-brand-black/30 ${
                      errors.email ? 'border-red-500' : 'border-brand-black/20'
                    }`}
                    data-testid="input-email"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                    Dirección (calle y número) *
                  </label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black ${
                      errors.street ? 'border-red-500' : 'border-brand-black/20'
                    }`}
                    data-testid="input-street"
                  />
                  {errors.street && (
                    <p className="text-red-400 text-xs mt-1">{errors.street}</p>
                  )}
                </div>

                {/* Colonia field */}
                <div>
                  <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                    Colonia *
                  </label>
                  <input
                    type="text"
                    name="neighborhood"
                    value={formData.neighborhood}
                    onChange={handleInputChange}
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black ${
                      errors.neighborhood ? 'border-red-500' : 'border-brand-black/20'
                    }`}
                    data-testid="input-neighborhood"
                  />
                  {errors.neighborhood && (
                    <p className="text-red-400 text-xs mt-1">{errors.neighborhood}</p>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div>
                    <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                      Ciudad *
                    </label>
                    {!showCustomCity ? (
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleCityChange}
                        className={`w-full px-2 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black appearance-none cursor-pointer ${
                          errors.city ? 'border-red-500' : 'border-brand-black/20'
                        }`}
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%231B4FD8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px' }}
                        data-testid="select-city"
                      >
                        <option value="">Selecciona tu ciudad</option>
                        {MEXICAN_CITIES.map((c) => (
                          <option key={c.city} value={c.city}>{c.city}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="Escribe tu ciudad"
                        className={`w-full px-2 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black placeholder:text-brand-black/30 ${
                          errors.city ? 'border-red-500' : 'border-brand-black/20'
                        }`}
                        data-testid="input-city-custom"
                      />
                    )}
                    {errors.city && (
                      <p className="text-red-400 text-xs mt-1">{errors.city}</p>
                    )}
                    {showCustomCity && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowCustomCity(false);
                          setFormData(prev => ({ ...prev, city: '', state: '' }));
                        }}
                        className="text-brand-cobalt text-xs mt-1 hover:underline"
                      >
                        ← Volver a lista
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                      Estado *
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      readOnly={!showCustomCity}
                      placeholder={showCustomCity ? "Escribe tu estado" : "Se llena automático"}
                      className={`w-full px-2 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black placeholder:text-brand-black/30 ${
                        !showCustomCity ? 'cursor-not-allowed opacity-70' : ''
                      } ${errors.state ? 'border-red-500' : 'border-brand-black/20'}`}
                      data-testid="input-state"
                    />
                    {errors.state && (
                      <p className="text-red-400 text-xs mt-1">{errors.state}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                      C.P. *
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      className={`w-full px-2 sm:px-4 py-2.5 sm:py-3 bg-white border rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black ${
                        errors.postal_code ? 'border-red-500' : 'border-brand-black/20'
                      }`}
                      data-testid="input-postal-code"
                    />
                    {errors.postal_code && (
                      <p className="text-red-400 text-xs mt-1">{errors.postal_code}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] sm:text-xs text-brand-black/60 uppercase tracking-wider mb-1.5 sm:mb-2 block">
                    Notas adicionales (opcional)
                  </label>
                  <textarea
                    name="customer_notes"
                    value={formData.customer_notes}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Instrucciones especiales..."
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-brand-black/20 rounded focus:outline-none focus:border-brand-cobalt text-sm sm:text-base text-brand-black placeholder:text-brand-black/30 resize-none"
                    data-testid="input-notes"
                  />
                </div>
              </div>

              <div className="flex justify-between mt-6 sm:mt-8">
                <button 
                  onClick={() => navigate('/cart')}
                  className="flex items-center gap-2 text-brand-black/60 hover:text-brand-cobalt transition-colors"
                >
                  <ChevronLeft size={18} />
                  Volver al carrito
                </button>
                <button 
                  onClick={nextStep}
                  className="brand-btn"
                  data-testid="continue-to-shipping-btn"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Shipping Method */}
          {currentStep === 2 && (
            <div data-testid="step-shipping">
              <h2 className="font-display text-2xl tracking-tight mb-6 text-brand-black">
                Método de Envío
              </h2>
              
              <div className="space-y-4">
                {SHIPPING_METHODS.map((method) => {
                  // Use promo shipping if available, otherwise fall back to standard logic
                  const actualShippingCost = getShippingCost();
                  const isFree = actualShippingCost === 0;
                  const displayPrice = shippingTotals?.promo_shipping_applied 
                    ? shippingTotals.shipping_cost 
                    : (isFree ? 0 : method.price);
                  
                  return (
                    <label
                      key={method.id}
                      className={`
                        flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 border rounded-lg cursor-pointer transition-all
                        ${formData.shipping_method === method.id 
                          ? 'border-brand-cobalt bg-brand-cobalt/5' 
                          : 'border-brand-black/20 hover:border-brand-cobalt/40'
                        }
                      `}
                      data-testid={`shipping-${method.id}`}
                    >
                      <div className="flex items-center gap-3 sm:gap-4">
                        <input
                          type="radio"
                          name="shipping_method"
                          value={method.id}
                          checked={formData.shipping_method === method.id}
                          onChange={handleInputChange}
                          className="w-5 h-5 accent-brand-cobalt flex-shrink-0"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Truck size={16} className="text-brand-cobalt sm:hidden" />
                            <Truck size={18} className="text-brand-cobalt hidden sm:block" />
                            <span className="font-medium text-brand-black text-sm sm:text-base">
                              {method.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock size={12} className="text-brand-black/40 sm:hidden" />
                            <Clock size={14} className="text-brand-black/40 hidden sm:block" />
                            <span className="text-xs sm:text-sm text-brand-black/50">
                              {method.description}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right mt-2 sm:mt-0 ml-8 sm:ml-0">
                        {isFree ? (
                          <>
                            <span className="line-through text-brand-black/40 text-xs sm:text-sm">
                              {formatPrice(100)}
                            </span>
                            <span className="block text-green-500 font-medium text-sm sm:text-base">Gratis</span>
                          </>
                        ) : (
                          <span className="font-medium text-brand-black text-sm sm:text-base">{formatPrice(displayPrice)}</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="flex justify-between mt-8">
                <button 
                  onClick={prevStep}
                  className="flex items-center gap-2 text-brand-black/60 hover:text-brand-cobalt transition-colors"
                >
                  <ChevronLeft size={18} />
                  Atrás
                </button>
                <button 
                  onClick={nextStep}
                  className="brand-btn"
                  data-testid="continue-to-payment-btn"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <div data-testid="step-payment">
              <h2 className="font-display text-xl sm:text-2xl tracking-tight mb-4 sm:mb-6 text-brand-black">
                Método de Pago
              </h2>
              
              <div className="space-y-3 sm:space-y-4">
                {/* Stripe Card Option with MSI info */}
                <label
                  className={`
                    block p-4 sm:p-6 border rounded-lg cursor-pointer transition-all
                    ${formData.payment_method === 'stripe' 
                      ? 'border-brand-cobalt bg-brand-cobalt/5' 
                      : 'border-brand-black/20 hover:border-brand-cobalt/40'
                    }
                  `}
                  data-testid="payment-stripe"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <input
                      type="radio"
                      name="payment_method"
                      value="stripe"
                      checked={formData.payment_method === 'stripe'}
                      onChange={handleInputChange}
                      className="w-5 h-5 accent-brand-cobalt mt-0.5 flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <span className="font-medium text-brand-black flex items-center gap-2 text-sm sm:text-base">
                        <CreditCard size={16} className="text-brand-cobalt flex-shrink-0" />
                        <span className="truncate">Tarjeta de Crédito/Débito</span>
                      </span>
                      <span className="text-xs sm:text-sm text-brand-black/50 block mt-1">
                        Visa, Mastercard, American Express
                      </span>
                      {/* MSI Badge */}
                      <div className="flex items-start gap-2 mt-2 sm:mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded">
                        <BadgePercent size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-[10px] sm:text-xs text-green-500 font-medium leading-tight">
                          Meses sin intereses disponibles (3, 6, 9 o 12 MSI)*
                        </span>
                      </div>
                    </div>
                  </div>
                </label>

                {/* OXXO Option */}
                <label
                  className={`
                    block p-4 sm:p-6 border rounded-lg cursor-pointer transition-all
                    ${formData.payment_method === 'oxxo' 
                      ? 'border-brand-cobalt bg-brand-cobalt/5' 
                      : 'border-brand-black/20 hover:border-brand-cobalt/40'
                    }
                  `}
                  data-testid="payment-oxxo"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <input
                      type="radio"
                      name="payment_method"
                      value="oxxo"
                      checked={formData.payment_method === 'oxxo'}
                      onChange={handleInputChange}
                      className="w-5 h-5 accent-brand-cobalt mt-0.5 flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <span className="font-medium text-brand-black flex items-center gap-2 text-sm sm:text-base">
                        <Store size={16} className="text-amber-400 flex-shrink-0" />
                        Pago en OXXO
                      </span>
                      <span className="text-xs sm:text-sm text-brand-black/50 block mt-1">
                        Paga en efectivo en cualquier tienda OXXO
                      </span>
                      <div className="flex items-start gap-2 mt-2 sm:mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded">
                        <Clock size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-[10px] sm:text-xs text-amber-400 leading-tight">
                          Recibirás un voucher. Tienes 3 días para realizar el pago.
                        </span>
                      </div>
                    </div>
                  </div>
                </label>

                {/* Bank Transfer Option */}
                <label
                  className={`
                    block p-4 sm:p-6 border rounded-lg cursor-pointer transition-all
                    ${formData.payment_method === 'transfer' 
                      ? 'border-brand-cobalt bg-brand-cobalt/5' 
                      : 'border-brand-black/20 hover:border-brand-cobalt/40'
                    }
                  `}
                  data-testid="payment-transfer"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <input
                      type="radio"
                      name="payment_method"
                      value="transfer"
                      checked={formData.payment_method === 'transfer'}
                      onChange={handleInputChange}
                      className="w-5 h-5 accent-brand-cobalt flex-shrink-0 mt-0.5"
                    />
                    <div className="min-w-0">
                      <span className="font-medium text-brand-black text-sm sm:text-base">
                        Transferencia Bancaria
                      </span>
                      <span className="text-xs sm:text-sm text-brand-black/50 block mt-1">
                        SPEI - Recibirás instrucciones por WhatsApp
                      </span>
                    </div>
                  </div>
                </label>
              </div>

              {/* MSI Disclaimer */}
              <p className="text-[10px] sm:text-xs text-brand-black/40 mt-4">
                * MSI disponible en tarjetas participantes. El número de meses depende de tu banco emisor.
              </p>

              {/* Order Review */}
              <div className="mt-6 sm:mt-8 p-4 sm:p-6 brand-card rounded-lg">
                <h3 className="font-display text-base sm:text-lg mb-3 sm:mb-4 text-brand-black">Resumen de envío</h3>
                <div className="space-y-2 text-xs sm:text-sm">
                  <p className="break-words"><span className="text-brand-black/50">Nombre:</span> <span className="text-brand-black">{formData.recipient_name}</span></p>
                  <p><span className="text-brand-black/50">Teléfono:</span> <span className="text-brand-black">{formData.phone}</span></p>
                  <p className="break-words"><span className="text-brand-black/50">Dirección:</span> <span className="text-brand-black">{formData.street}</span></p>
                  <p className="break-words"><span className="text-brand-black/50">Ciudad:</span> <span className="text-brand-black">{formData.city}, {formData.state} {formData.postal_code}</span></p>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <button 
                  onClick={prevStep}
                  className="flex items-center gap-2 text-brand-black/60 hover:text-brand-cobalt transition-colors"
                >
                  <ChevronLeft size={18} />
                  Atrás
                </button>
                <button 
                  onClick={handleSubmitOrder}
                  disabled={loading}
                  className="brand-btn disabled:opacity-50"
                  data-testid="place-order-btn"
                >
                  {loading ? 'Procesando...' : 'Confirmar Pedido'}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Embedded Payment */}
          {currentStep === 4 && createdOrder && !orderComplete && (
            <div data-testid="step-embedded-payment">
              <h2 className="font-display text-xl sm:text-2xl tracking-tight mb-2 text-brand-black">
                Pago Seguro
              </h2>
              <p className="text-brand-black/50 text-sm mb-6">
                Completa tu pago con tarjeta de crédito o débito
              </p>
              
              {/* Order Summary in Payment Step */}
              <div className="brand-card p-4 sm:p-6 rounded-lg mb-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-brand-black/10">
                  <div>
                    <p className="text-xs text-brand-black/50">Orden</p>
                    <p className="font-mono text-sm text-brand-cobalt">{createdOrder.order_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-brand-black/50">Total a pagar</p>
                    <p className="font-display text-xl text-brand-black">{formatPrice(createdOrder.total)}</p>
                  </div>
                </div>
                
                <div className="space-y-2 text-xs text-brand-black/60">
                  <p><span className="text-brand-black/40">Envío a:</span> {formData.recipient_name}</p>
                  <p className="truncate"><span className="text-brand-black/40">Dirección:</span> {formData.street}, {formData.city}</p>
                </div>
              </div>
              
              {/* Stripe Embedded Form */}
              <div className="bg-white rounded border border-brand-black/10 p-4 sm:p-6">
                <StripeEmbeddedCheckout
                  orderId={createdOrder.id}
                  amount={createdOrder.total}
                  paymentMethodType={formData.payment_method === 'oxxo' ? 'oxxo' : 'card'}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              </div>

              {/* Back button */}
              <button 
                onClick={() => {
                  setCreatedOrder(null);
                  setCurrentStep(3);
                }}
                className="flex items-center gap-2 text-brand-black/60 hover:text-brand-cobalt transition-colors mt-6"
              >
                <ChevronLeft size={18} />
                Cambiar método de pago
              </button>
            </div>
          )}

          {/* Step 5: Confirmation */}
          {currentStep === 5 && orderComplete && (
            <div data-testid="step-confirmation" className="text-center py-8 sm:py-12 px-2">
              {/* Logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vng9vv17_logo-alphatica-blanco.png"
                alt="Alphatica"
                className="h-10 sm:h-14 w-auto mx-auto mb-6 sm:mb-8 opacity-60"
              />
              
              {oxxoVoucher ? (
                // OXXO Voucher Confirmation
                <>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-500/20 border border-amber-500/30 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <Store className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400" />
                  </div>
                  
                  <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-2 text-brand-black">
                    ¡Voucher OXXO Generado!
                  </h2>
                  <p className="text-brand-black/50 mb-6 sm:mb-8 text-sm sm:text-base">
                    Realiza tu pago en cualquier OXXO
                  </p>

                  <div className="brand-card p-4 sm:p-6 max-w-md mx-auto text-left mb-6 rounded-lg">
                    <div className="text-center mb-4">
                      <p className="text-xs text-brand-black/50 mb-1">Número de referencia</p>
                      <p className="font-mono text-2xl text-brand-cobalt tracking-wider">{oxxoVoucher.number}</p>
                    </div>
                    
                    <div className="border-t border-brand-black/10 pt-4 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-brand-black/50">Total a pagar:</span>
                        <span className="font-medium text-brand-black">{formatPrice(orderComplete.total)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-brand-black/50">Expira:</span>
                        <span className="text-amber-400">
                          {new Date(oxxoVoucher.expires_after * 1000).toLocaleDateString('es-MX')}
                        </span>
                      </div>
                    </div>
                    
                    {oxxoVoucher.hosted_voucher_url && (
                      <a 
                        href={oxxoVoucher.hosted_voucher_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full mt-4 py-3 px-4 bg-amber-500 text-white font-medium rounded-lg text-center hover:bg-amber-400 transition-colors"
                      >
                        Ver Voucher Completo
                      </a>
                    )}
                  </div>

                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 max-w-md mx-auto mb-6 text-left">
                    <p className="text-xs text-amber-400 font-medium mb-2">Instrucciones:</p>
                    <ol className="text-xs text-amber-300/80 space-y-1 list-decimal list-inside">
                      <li>Acude a cualquier tienda OXXO</li>
                      <li>Indica que realizarás un pago de servicio</li>
                      <li>Proporciona el número de referencia</li>
                      <li>Paga el monto exacto en efectivo</li>
                    </ol>
                  </div>
                </>
              ) : (
                // Card Payment Success
                <>
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-500" />
                  </div>
                  
                  <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-2 text-brand-black">
                    ¡Pago Exitoso!
                  </h2>
                  <p className="text-brand-black/50 mb-6 sm:mb-8 text-sm sm:text-base">
                    Tu pedido ha sido confirmado y está siendo procesado
                  </p>

                  <div className="brand-card p-4 sm:p-8 max-w-md mx-auto text-left mb-6 sm:mb-8 rounded-lg">
                    <p className="text-[10px] sm:text-xs text-brand-cobalt uppercase tracking-wider mb-1">
                      Número de orden
                    </p>
                    <p className="font-display text-lg sm:text-2xl text-brand-black mb-3 sm:mb-4 break-all">
                      {orderComplete.order_number}
                    </p>
                    
                    <div className="space-y-2 text-xs sm:text-sm border-t border-brand-black/10 pt-3 sm:pt-4">
                      <p>
                        <span className="text-brand-black/50">Total:</span>{' '}
                        <span className="font-medium text-brand-black">{formatPrice(orderComplete.total)}</span>
                      </p>
                      <p>
                        <span className="text-brand-black/50">Estado:</span>{' '}
                        <span className="text-green-500">Pagado</span>
                      </p>
                    </div>
                  </div>
                </>
              )}

              <p className="text-xs sm:text-sm text-brand-black/50 mb-6 sm:mb-8 px-2">
                Te enviaremos actualizaciones por WhatsApp al número {formData.phone}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button 
                  onClick={() => navigate('/products')}
                  className="brand-btn text-sm sm:text-base flex items-center justify-center gap-2"
                >
                  Seguir Comprando
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary Sidebar */}
        {currentStep < 4 && (
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="brand-card p-3 sm:p-4 md:p-6 rounded-lg lg:sticky lg:top-32 overflow-hidden">
              <h3 className="font-display text-base sm:text-lg md:text-xl tracking-tight mb-3 sm:mb-4 md:mb-6 text-brand-black">
                Tu Pedido
              </h3>

              {/* Items */}
              <div className="space-y-3 sm:space-y-4 max-h-48 sm:max-h-64 overflow-y-auto mb-4 sm:mb-6">
                {items.map((item) => (
                  <div key={`${item.product_id}-${item.size_ml || 100}`} className="flex gap-2 sm:gap-3">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-brand-black/5 flex-shrink-0 rounded overflow-hidden">
                      <img
                        src={item.main_image || 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=100'}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className="text-xs sm:text-sm font-medium text-brand-black line-clamp-2 leading-tight">
                        {item.product_name}
                      </p>
                      <p className="text-[10px] sm:text-xs text-brand-black/50">
                        {(item.size_ml || 100) === 40 ? 35 : (item.size_ml || 100)}ml • Cant: {item.quantity}
                      </p>
                      <p className="text-xs sm:text-sm font-medium text-brand-black">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="space-y-2 sm:space-y-3 border-t border-brand-black/10 pt-3 sm:pt-4">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-brand-black/60">Subtotal</span>
                  <span className="text-brand-black font-medium">{formatPrice(totals.subtotal)}</span>
                </div>
                
                {(totals.promo_discount || 0) > 0 && (
                  <div className="flex justify-between text-xs sm:text-sm text-amber-500" data-testid="checkout-promo-discount">
                    <span>
                      {(totals.applied_promos || []).filter((p) => p.discount_amount > 0).map((p) => p.promo_name).join(', ') || 'Promoción'}
                    </span>
                    <span className="font-medium">-{formatPrice(totals.promo_discount)}</span>
                  </div>
                )}

                {gifts && gifts.applied && gifts.picks.length > 0 && (
                  <div className="text-xs sm:text-sm text-brand-black/70" data-testid="checkout-gifts">
                    <span className="font-medium text-brand-black">Regalo 10ml:</span>{' '}
                    {gifts.picks.map((p) => p.name).join(', ')}
                  </div>
                )}

                {totals.discount_amount > 0 && (
                  <div className="flex justify-between text-xs sm:text-sm text-green-500">
                    <span>Descuento</span>
                    <span className="font-medium">-{formatPrice(totals.discount_amount)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-brand-black/60">Envío</span>
                  <span className="font-medium">
                    {getShippingCost() === 0 ? (
                      <span className="text-green-500">Gratis</span>
                    ) : (
                      <span className="text-brand-black">{formatPrice(getShippingCost())}</span>
                    )}
                  </span>
                </div>
                
                <div className="flex justify-between pt-2 sm:pt-3 border-t border-brand-black/10">
                  <span className="font-display text-sm sm:text-lg text-brand-black">Total</span>
                  <span className="font-display text-base sm:text-xl text-brand-black font-bold">
                    {formatPrice(getFinalTotal())}
                  </span>
                </div>
              </div>

              {coupon && (
                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 text-center rounded">
                  <p className="text-xs text-green-500">
                    Cupón {coupon.code} aplicado
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
